import type {
  CrmContactOperatorBrief,
  CrmContactOperatorBriefPoint,
  CrmContactOperatorBriefPriority,
  CrmContactOperatorBriefTone,
} from "@ease/crm/contact-operator-brief";
import { cn } from "@/lib/utils";

interface CrmOperatorBriefCardProps {
  brief: CrmContactOperatorBrief | null;
  title?: string;
  className?: string;
}

export function CrmOperatorBriefCard({
  brief,
  title = "Operator brief",
  className,
}: CrmOperatorBriefCardProps) {
  if (!brief) return null;

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{brief.label}</p>
          {brief.summary ? (
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{brief.summary}</p>
          ) : null}
        </div>
        {brief.scoreLabel ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold",
            getCrmOperatorBriefToneClasses(brief.scoreTone),
          )}>
            {brief.scoreLabel}
          </span>
        ) : null}
      </div>

      {brief.points.length > 0 ? (
        <div className="mt-4 grid gap-2">
          {brief.points.map((point) => (
            <OperatorBriefPointCard key={`${brief.key}:point:${point.key}`} point={point} />
          ))}
        </div>
      ) : null}

      {(brief.objections.length > 0 || brief.nextSteps.length > 0) && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {brief.objections.length > 0 ? (
            <OperatorBriefPointList title="Objection handling" points={brief.objections} />
          ) : null}
          {brief.nextSteps.length > 0 ? (
            <OperatorBriefPointList title="Next steps" points={brief.nextSteps} />
          ) : null}
        </div>
      )}
    </section>
  );
}

function OperatorBriefPointList({
  title,
  points,
}: {
  title: string;
  points: CrmContactOperatorBriefPoint[];
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        {title}
      </p>
      <div className="mt-3 space-y-2">
        {points.map((point) => (
          <OperatorBriefPointCard key={`${title}:${point.key}`} point={point} compact />
        ))}
      </div>
    </div>
  );
}

function OperatorBriefPointCard({
  point,
  compact = false,
}: {
  point: CrmContactOperatorBriefPoint;
  compact?: boolean;
}) {
  return (
    <div className={cn(
      "rounded-lg border bg-[var(--color-background)] px-3 py-2",
      getCrmOperatorBriefBorderClasses(point.tone),
      compact && "bg-[var(--color-card)]",
    )}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-[var(--color-foreground)]">{point.label}</p>
            <OperatorBriefPriorityChip priority={point.priority} />
            {point.category ? (
              <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                {point.category.replaceAll("_", " ")}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-[var(--color-foreground)]">{point.summary}</p>
          {point.detail ? (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{point.detail}</p>
          ) : null}
        </div>
        <span className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
          getCrmOperatorBriefToneClasses(point.tone),
        )}>
          {point.tone}
        </span>
      </div>
      {point.evidence ? (
        <p className="mt-2 rounded-md bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {point.evidence}
        </p>
      ) : null}
    </div>
  );
}

function OperatorBriefPriorityChip({ priority }: { priority: CrmContactOperatorBriefPriority }) {
  const label = priority === "critical" ? "Critical" : priority.charAt(0).toUpperCase() + priority.slice(1);
  return (
    <span className={cn(
      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]",
      priority === "critical"
        ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
        : priority === "high"
          ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
          : "bg-[var(--color-primary-light)] text-[var(--color-primary)]",
    )}>
      {label}
    </span>
  );
}

export function getCrmOperatorBriefToneClasses(tone: CrmContactOperatorBriefTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

export function getCrmOperatorBriefBorderClasses(tone: CrmContactOperatorBriefTone) {
  if (tone === "success") return "border-[var(--color-success)]/25";
  if (tone === "warning") return "border-[var(--color-warning)]/25";
  if (tone === "danger") return "border-[var(--color-destructive)]/25";
  if (tone === "info") return "border-[var(--color-info)]/25";
  return "border-[var(--color-border)]";
}
